import { useLocation, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, Check, ArrowRight, ExternalLink } from "lucide-react";
import { PRODUCTS } from "../lib/products";
import AnimatedCounter from "../components/AnimatedCounter";
import FAQAccordion from "../components/FAQAccordion";
import CrossSell from "../components/CrossSell";
import MagneticButton from "../components/MagneticButton";
import ParticleField from "../components/ParticleField";
import WalletBadges from "../components/WalletBadges";
import SEOHead, { softwareAppSchema, faqSchema, breadcrumbSchema, orgSchema } from "../components/SEOHead";
import { analytics } from "../lib/analytics";

// Products whose customer-facing pass works with Apple + Google Wallet
const WALLET_PRODUCTS = new Set(["loyalty", "connect"]);

// Map slug → SoftwareApplication category (Schema.org)
const PRODUCT_CATEGORY = {
  loyalty: "BusinessApplication",
  review: "BusinessApplication",
  connect: "BusinessApplication",
  page: "BusinessApplication",
  booking: "BusinessApplication",
  luckywheel: "BusinessApplication",
};

const ProductDetail = () => {
  const location = useLocation();
  const slug = location.pathname.replace(/^\//, "").split("/")[0];
  const { t } = useTranslation();
  const product = PRODUCTS[slug];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    // GA4 view_item — used by Google Ads for remarketing audiences + product performance
    if (slug && PRODUCTS[slug]) analytics.productPageView(slug);
  }, [slug]);

  if (!product) return <Navigate to="/" replace />;

  // i18n content
  const c = t(`products.${slug}`, { returnObjects: true });

  const Icon = product.icon;
  const accent = product.accentColor;
  const glow = product.accentGlow;
  const isComing = product.status === "coming_soon";

  // Hostname of live URL for the read-more chip
  let liveHost = "";
  try { liveHost = new URL(product.liveUrl).host.replace(/^www\./, ""); } catch (e) { liveHost = product.liveUrl; }

  // -------- SEO schemas --------
  const seoTitle = `${product.name} — ${c.short}`;
  const seoDesc = c.long || c.short;
  const faqs = Array.isArray(c.faqs) ? c.faqs : [];
  const productSchemas = [
    softwareAppSchema({
      name: product.name,
      description: seoDesc,
      url: `https://tryb.business/${slug}`,
      image: `https://tryb.business${product.desktopMockup || "/android-chrome-512x512.png"}`,
      category: PRODUCT_CATEGORY[slug] || "BusinessApplication",
    }),
    breadcrumbSchema([
      { name: "TRYB", path: "/" },
      { name: "Solutions", path: "/#solutions" },
      { name: product.name, path: `/${slug}` },
    ]),
    orgSchema(),
  ];
  if (faqs.length) productSchemas.push(faqSchema(faqs));

  return (
    <div className="grain-overlay">
      <SEOHead
        title={seoTitle}
        description={seoDesc}
        path={`/${slug}`}
        image={product.desktopMockup ? `https://tryb.business${product.desktopMockup}` : undefined}
        type="product"
        schemas={productSchemas}
      />
      {/* HERO */}
      <section className="relative pt-32 pb-20 overflow-hidden" data-testid={`product-hero-${slug}`}>
        <div className="absolute inset-0 grid-bg opacity-40" aria-hidden="true" />
        <ParticleField color="180, 200, 255" density={0.00007} />
        <div
          className="absolute -top-32 right-0 h-[640px] w-[640px] rounded-full blur-[120px] opacity-50 pointer-events-none"
          style={{ background: `radial-gradient(circle, ${accent}aa, transparent 60%)` }}
        />
        <div
          className="absolute -bottom-40 -left-32 h-[520px] w-[520px] rounded-full blur-[120px] opacity-40 pointer-events-none"
          style={{ background: `radial-gradient(circle, hsl(270, 100%, 60%, 0.6), transparent 60%)` }}
        />

        <div className="relative mx-auto max-w-7xl px-5 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="flex items-center gap-3"
            >
              <div
                className="flex h-12 w-12 items-center justify-center rounded-2xl"
                style={{
                  background: `linear-gradient(135deg, ${accent}, transparent)`,
                  boxShadow: `inset 0 0 0 1px ${accent}55, 0 20px 60px -20px ${glow}`,
                }}
              >
                <Icon className="h-6 w-6 text-white" strokeWidth={1.6} />
              </div>
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold" style={{ color: accent }}>{product.name}</p>
                {isComing && (
                  <span
                    className="rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em]"
                    style={{ background: `${accent}22`, color: accent }}
                    data-testid={`product-status-${slug}`}
                  >
                    {t("common.comingSoon")}
                  </span>
                )}
              </div>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05]"
              data-testid={`product-title-${slug}`}
            >
              {c.tagline}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-6 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed"
            >
              {c.longHero}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-9 flex flex-wrap items-center gap-3"
            >
              <MagneticButton>
                <a
                  href={product.liveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => analytics.visitLiveProduct(slug, product.liveUrl)}
                  className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white transition"
                  style={{
                    background: `linear-gradient(135deg, ${accent}, hsl(var(--tryb-violet)))`,
                    boxShadow: `0 14px 40px -10px ${glow}`,
                  }}
                  data-testid={`product-cta-live-${slug}`}
                >
                  {isComing ? t("product.comingCta") : t("product.liveCta")}
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </MagneticButton>
              <MagneticButton>
                <Link to="/#contact" className="btn-ghost" data-testid={`product-cta-contact-${slug}`}>
                  {t("nav.getStarted")}
                </Link>
              </MagneticButton>
            </motion.div>

            {/* Read more chip pointing to live website */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.45 }}
              className="mt-6"
            >
              <a
                href={product.liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-foreground/10 bg-foreground/[0.03] px-4 py-2 text-xs font-medium text-foreground/80 hover:text-foreground hover:bg-foreground/[0.07] transition"
                data-testid={`product-readmore-${slug}`}
              >
                <ExternalLink className="h-3.5 w-3.5" style={{ color: accent }} />
                <span>{t("product.readMore")}</span>
                <span className="text-muted-foreground">·</span>
                <span className="font-semibold" style={{ color: accent }}>{liveHost}</span>
              </a>
            </motion.div>

            {/* Wallet badges (only for products with wallet pass support) */}
            {WALLET_PRODUCTS.has(slug) && (
              <WalletBadges slug={slug} className="mt-6" />
            )}

            {/* stats */}
            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-xl">
              {product.statsValues.map((s, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 + i * 0.06 }}
                  className="rounded-xl border border-foreground/10 bg-foreground/[0.02] p-4"
                  data-testid={`product-stat-${slug}-${i}`}
                >
                  <p className="font-display text-2xl" style={{ color: accent }}>
                    <AnimatedCounter value={s.value} suffix={s.suffix} />
                  </p>
                  <p className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">{c.statsLabels[i]}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Mockup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.2, 0.7, 0.2, 1] }}
            className="lg:col-span-6 relative"
            data-testid={`product-mockup-${slug}`}
          >
            <div
              className="absolute -inset-10 rounded-[3rem] blur-3xl opacity-50"
              style={{ background: `radial-gradient(ellipse, ${accent}55, transparent 60%)` }}
              aria-hidden
            />
            <div className="relative rounded-3xl border border-foreground/10 bg-foreground/[0.02] p-3 md:p-4 ring-glow">
              <img
                src={product.mockups.laptop}
                alt={`${product.name} dashboard`}
                className="w-full h-auto rounded-2xl object-cover aspect-[4/3] bg-foreground/5"
                loading="lazy"
              />
            </div>
            <div className="absolute -bottom-10 right-2 md:-right-4 w-40 sm:w-48 md:w-60 rounded-[28px] border border-foreground/10 bg-foreground/[0.04] p-1.5 md:p-2 shadow-2xl rotate-3">
              <img
                src={product.mockups.mobile}
                alt={`${product.name} mobile preview`}
                className="w-full h-auto rounded-2xl object-contain bg-white"
                loading="lazy"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="relative py-24 md:py-32" data-testid={`product-features-${slug}`}>
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <p className="text-[10px] md:text-xs font-bold uppercase tracking-[0.24em] text-muted-foreground">
            {t("product.whatItDoes")}
          </p>
          <h2 className="font-display mt-4 max-w-3xl text-4xl md:text-5xl tracking-tighter leading-[1.05]">
            {t("product.featuresIntroPre")} {product.name}.
          </h2>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {c.features.map((f, i) => {
              const FIcon = product.featureIcons[i] || product.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  className="group rounded-2xl border border-foreground/10 bg-foreground/[0.02] p-7 hover:bg-foreground/[0.05] hover:border-foreground/20 transition"
                  data-testid={`product-feature-${slug}-${i}`}
                >
                  <div
                    className="flex h-11 w-11 items-center justify-center rounded-xl"
                    style={{ background: `${accent}1a`, boxShadow: `inset 0 0 0 1px ${accent}55` }}
                  >
                    <FIcon className="h-5 w-5" style={{ color: accent }} strokeWidth={1.6} />
                  </div>
                  <h3 className="font-display mt-6 text-xl">{f.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* USE CASES + BENEFITS */}
      <section className="relative py-20" data-testid={`product-benefits-${slug}`}>
        <div className="mx-auto max-w-7xl px-5 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <p className="text-[10px] md:text-xs font-bold uppercase tracking-[0.24em] text-muted-foreground">
              {t("product.useCases")}
            </p>
            <h2 className="font-display mt-4 text-3xl md:text-4xl tracking-tighter leading-[1.1]">
              {t("product.useCasesTitle")}
            </h2>
            <p className="mt-5 text-base text-muted-foreground leading-relaxed">{c.audience}</p>
          </div>
          <div className="lg:col-span-7">
            <p className="text-[10px] md:text-xs font-bold uppercase tracking-[0.24em] text-muted-foreground">
              {t("product.keyBenefits")}
            </p>
            <ul className="mt-6 space-y-3">
              {c.benefits.map((b, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.07 }}
                  className="flex items-start gap-3 rounded-xl border border-foreground/10 bg-foreground/[0.02] p-4"
                  data-testid={`product-benefit-${slug}-${i}`}
                >
                  <span
                    className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                    style={{ background: `${accent}22`, boxShadow: `inset 0 0 0 1px ${accent}66` }}
                  >
                    <Check className="h-3.5 w-3.5" style={{ color: accent }} strokeWidth={2.5} />
                  </span>
                  <p className="text-sm md:text-[15px] text-foreground/90">{b}</p>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="relative py-24 md:py-32 overflow-hidden" data-testid={`product-how-${slug}`}>
        <div className="absolute inset-0 grid-bg opacity-30" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-5 md:px-8">
          <p className="text-[10px] md:text-xs font-bold uppercase tracking-[0.24em] text-muted-foreground">
            {t("product.howItWorks")}
          </p>
          <h2 className="font-display mt-4 max-w-2xl text-4xl md:text-5xl tracking-tighter leading-[1.05]">
            {t("product.howTitle")}
          </h2>

          <div className={`mt-14 grid grid-cols-1 md:grid-cols-2 ${c.howItWorks.length === 3 ? "lg:grid-cols-3" : "lg:grid-cols-4"} gap-5 relative`}>
            {c.howItWorks.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="relative rounded-2xl border border-foreground/10 bg-foreground/[0.02] p-7"
                data-testid={`product-step-${slug}-${i}`}
              >
                <p className="font-display text-4xl" style={{ color: accent }}>
                  0{i + 1}
                </p>
                <h3 className="font-display mt-6 text-lg">{step.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{step.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="relative py-24 md:py-32" data-testid={`product-pricing-${slug}`}>
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <p className="text-[10px] md:text-xs font-bold uppercase tracking-[0.24em] text-muted-foreground">
            {t("product.pricingTitle")}
          </p>
          <h2 className="font-display mt-4 max-w-2xl text-4xl md:text-5xl tracking-tighter leading-[1.05]">
            {t("product.pricingSubtitle")}
          </h2>

          <div className={`mt-14 grid grid-cols-1 md:grid-cols-2 ${product.pricing.length >= 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-5`}>
            {product.pricing.map((tier, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className={`relative rounded-2xl border p-7 transition ${
                  tier.featured
                    ? "border-transparent bg-foreground/[0.04]"
                    : "border-foreground/10 bg-foreground/[0.02]"
                }`}
                style={
                  tier.featured
                    ? { boxShadow: `inset 0 0 0 1px ${accent}88, 0 20px 60px -20px ${glow}` }
                    : undefined
                }
                data-testid={`product-pricing-tier-${slug}-${i}`}
              >
                {tier.featured && (
                  <span
                    className="absolute top-4 end-4 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em]"
                    style={{ background: `${accent}33`, color: accent }}
                  >
                    {t("product.popular")}
                  </span>
                )}
                <p className="text-sm font-semibold text-foreground/90">{c.pricingNames[i]}</p>
                <p className="font-display mt-4 text-4xl tracking-tight">
                  {tier.price}
                  <span className="text-sm text-muted-foreground font-medium">{tier.per}</span>
                </p>
                <p className="mt-3 text-sm text-muted-foreground">{c.pricingNotes[i]}</p>
                <a
                  href={product.liveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 inline-flex items-center gap-1 text-sm font-semibold"
                  style={{ color: accent }}
                  data-testid={`pricing-cta-${slug}-${i}`}
                >
                  {isComing ? t("product.comingCta") : t("product.getStarted")}
                  <ArrowRight className="h-3.5 w-3.5" />
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative py-20 md:py-24" data-testid={`product-faq-${slug}`}>
        <div className="mx-auto max-w-3xl px-5 md:px-8">
          <p className="text-[10px] md:text-xs font-bold uppercase tracking-[0.24em] text-muted-foreground text-center">
            {t("product.faqLabel")}
          </p>
          <h2 className="font-display mt-4 text-4xl md:text-5xl tracking-tighter leading-[1.05] text-center">
            {t("product.faqTitle")}
          </h2>
          <div className="mt-10">
            <FAQAccordion items={c.faqs} />
          </div>
        </div>
      </section>

      <CrossSell slug={slug} />
    </div>
  );
};

export default ProductDetail;
